import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import * as Icons from "lucide-react";
import { portalApi } from "@/lib/portalApi";
import { usePortalAuth } from "@/context/PortalAuthContext";
import { toast } from "sonner";
import { EngagementBreakdown } from "@/components/EngagementBreakdown";
import { equivalentFor } from "@/lib/currency";
import { FxDisclaimer } from "@/components/shared/DualPrice";

const money = (n, cur = "USD") => new Intl.NumberFormat("en-US", { style: "currency", currency: cur }).format(n || 0);

export default function PortalProposal() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { refresh } = usePortalAuth();
  const [prop, setProp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cc, setCc] = useState({ usd_thb_rate: 33, show_thb: false });

  const load = useCallback(async () => {
    try { const { data } = await portalApi.get(`/portal/proposals/${id}`); setProp(data); }
    catch { setError("This estimate is unavailable."); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { portalApi.get("/portal/currency-context").then((r) => r?.data && setCc(r.data)).catch(() => {}); }, []);

  const accept = async () => {
    if (!agree) return toast.error("Please confirm you agree to the estimated charges.");
    setBusy(true);
    try {
      await portalApi.post(`/portal/proposals/${id}/accept`, { agree: true });
      setProp((p) => ({ ...p, status: "accepted" }));
      toast.success("Estimate accepted — your engagement letter is being prepared!");
      refresh?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not accept"); }
    finally { setBusy(false); }
  };

  if (loading) return <div className="flex justify-center py-20"><Icons.Loader2 className="h-6 w-6 animate-spin text-[#F5A623]" /></div>;
  if (error) return <div className="text-center py-20 text-slate-500"><Icons.AlertTriangle className="h-8 w-8 text-[#F5A623] mx-auto mb-2" />{error}</div>;

  const cur = prop.currency || "USD";
  const accepted = prop.status === "accepted";
  const eq = (v) => equivalentFor(v, cur, { rate: cc.usd_thb_rate, showThb: cc.show_thb });

  return (
    <div className="max-w-3xl mx-auto" data-testid="portal-proposal">
      <button onClick={() => navigate("/portal")} className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-[#171B2E] mb-4"><Icons.ArrowLeft className="h-4 w-4" />Back to dashboard</button>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        <div className="bg-[#171B2E] text-white px-6 py-5">
          <div className="text-xs text-[#F5A623] font-mono">{prop.proposal_number}</div>
          <h1 className="font-heading text-2xl font-bold mt-0.5">{prop.title || "Service Estimate"}</h1>
        </div>
        <div className="p-6 space-y-5">
          {prop.scope_summary && (
            <div><h2 className="font-heading font-semibold text-[#171B2E] mb-1.5">Scope of Services</h2>
              <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{prop.scope_summary}</p></div>
          )}

          <div>
            <h2 className="font-heading font-semibold text-[#171B2E] mb-2">Estimated Charges</h2>
            <div className="border border-slate-200 rounded-xl overflow-hidden" data-testid="portal-proposal-items">
              {(prop.line_items || []).map((li) => (
                <div key={li.id} className="flex items-start justify-between gap-4 px-4 py-3 border-b border-slate-100 last:border-0">
                  <div><div className="text-sm font-medium text-[#171B2E]">{li.name || li.description || "Service"}</div>
                    {li.description && li.name && <div className="text-xs text-slate-400">{li.description}</div>}</div>
                  <div className="text-sm font-medium text-[#171B2E] whitespace-nowrap text-right"><div>{money(li.amount, cur)}</div>{eq(li.amount) && <div className="text-[11px] font-normal text-slate-400">≈ {eq(li.amount)}</div>}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 space-y-1 text-sm">
              <div className="flex justify-between text-slate-500"><span>Subtotal</span><span>{money(prop.subtotal, cur)}{eq(prop.subtotal) && <span className="text-[11px] text-slate-400 ml-1">≈ {eq(prop.subtotal)}</span>}</span></div>
              {prop.discount > 0 && <div className="flex justify-between text-emerald-600"><span>Discount</span><span>−{money(prop.discount, cur)}</span></div>}
              <div className="flex justify-between font-semibold text-[#171B2E] text-base pt-1.5 border-t border-slate-100"><span>Total Estimated</span><span className="text-right" data-testid="portal-proposal-total"><div>{money(prop.total, cur)}</div>{eq(prop.total) && <div className="text-[11px] font-normal text-slate-400">≈ {eq(prop.total)}</div>}</span></div>
            </div>

            <EngagementBreakdown breakdown={prop.engagement_breakdown} rate={cc.usd_thb_rate} showThb={cc.show_thb} />
            {(eq(prop.total) || cc.show_thb) && !(prop.engagement_breakdown && Object.keys(prop.engagement_breakdown).length > 0) && <FxDisclaimer className="mt-2" />}
          </div>

          {prop.payment_terms && <p className="text-xs text-slate-500"><span className="font-medium">Payment terms:</span> {prop.payment_terms}</p>}

          {accepted ? (
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5 text-center" data-testid="portal-proposal-accepted">
              <Icons.CheckCircle2 className="h-10 w-10 text-emerald-600 mx-auto mb-2" />
              <div className="font-semibold text-emerald-800">Estimate Accepted</div>
              <p className="text-sm text-emerald-700 mt-0.5">Your engagement letter is being prepared — you'll see it on your dashboard shortly.</p>
              <button onClick={() => navigate("/portal")} className="mt-4 inline-flex items-center gap-2 bg-[#171B2E] hover:bg-[#222842] text-white text-sm rounded-lg px-4 py-2">Back to dashboard<Icons.ArrowRight className="h-4 w-4 text-[#F5A623]" /></button>
            </div>
          ) : (
            <div className="border-t border-slate-100 pt-5">
              <label className="flex items-start gap-2.5 text-sm text-slate-700 bg-slate-50 rounded-lg p-3.5 mb-4">
                <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-0.5 accent-[#F5A623]" data-testid="portal-proposal-agree" />
                <span className="font-medium">I agree to the estimated charges outlined in this estimate.</span>
              </label>
              <button onClick={accept} disabled={busy} data-testid="portal-proposal-accept-btn"
                className="w-full bg-[#F5A623] hover:bg-[#e0951a] disabled:opacity-60 text-[#171B2E] font-semibold rounded-lg py-3 flex items-center justify-center gap-2">
                {busy ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.CheckCircle className="h-4 w-4" />}Accept Estimate
              </button>
            </div>
          )}
        </div>
      </div>
      <p className="text-center text-xs text-slate-400 mt-4">Secured by Tax Flow · Expat Tax Firm</p>
    </div>
  );
}
